import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { CheckCircle, TrendingUp, Users, Award, MapPin, MessageCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BecomeDealer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    businessExperience: '',
    warehouseSpace: '',
    currentBusiness: '',
    investmentCapacity: '',
    additionalInfo: ''
  });

  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const experienceLabels = {
      '0-2': '0-2 years',
      '2-5': '2-5 years',
      '5-10': '5-10 years',
      '10+': '10+ years'
    };
    const warehouseLabels = {
      'less-500': 'Less than 500 sq ft',
      '500-1000': '500-1000 sq ft',
      '1000-2000': '1000-2000 sq ft',
      '2000+': '2000+ sq ft'
    };
    const investmentLabels = {
      '1-3': '1-3 Lakhs',
      '3-5': '3-5 Lakhs',
      '5-10': '5-10 Lakhs',
      '10+': '10+ Lakhs'
    };

    const payload = {
      'Full Name': formData.name,
      'Phone': formData.phone,
      'Email': formData.email,
      'City': formData.city,
      'State': formData.state,
      'Business Experience': experienceLabels[formData.businessExperience] || formData.businessExperience,
      'Warehouse Space': warehouseLabels[formData.warehouseSpace] || formData.warehouseSpace,
      'Current Business Type': formData.currentBusiness || 'Not specified',
      'Investment Capacity': investmentLabels[formData.investmentCapacity] || formData.investmentCapacity,
      'Additional Information': formData.additionalInfo || 'None',
      _subject: 'New Dealer Application - Arihant Milk Products',
      _template: 'table',
      _captcha: 'false',
      _replyto: formData.email
    };

    try {
      const res = await fetch(
        'https://formsubmit.co/ajax/samarthsurana@arihantmilkproducts.com',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) throw new Error('Request failed');

      toast.success('Application submitted successfully! We will contact you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        state: '',
        businessExperience: '',
        warehouseSpace: '',
        currentBusiness: '',
        investmentCapacity: '',
        additionalInfo: ''
      });
    } catch (error) {
      console.error('Error submitting dealer application:', error);
      toast.error('Submission failed. Please WhatsApp or email us directly using the buttons below.');
    } finally {
      setSubmitting(false);
    }
  };

  const dealerTestimonials = [
    {
      name: "Rajesh Jain",
      city: "",
      feedback: "Partnering with Arihant Milk Products has been the best business decision. The profit margins are excellent and products sell themselves due to superior quality.",
      rating: 5
    },
    {
      name: "Amit Jain",
      city: "",
      feedback: "Amazing support from the company. Regular supply, good margins, and growing customer demand make this the most profitable dairy dealership in my region.",
      rating: 5
    },
    {
      name: "Priya Patel",
      city: "",
      feedback: "I started 2 years ago and now have 3 distribution points. The brand recognition and product quality ensure consistent growth and profits.",
      rating: 5
    },
    {
      name: "Sandeep Singh",
      city: "",
      feedback: "Best dairy brand partnership in Central India. Strong margins, excellent product range, and professional business support from day one.",
      rating: 5
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: "High Profit Margins",
      description: "Enjoy industry-leading profit margins on all product categories"
    },
    {
      icon: Award,
      title: "Premium Brand",
      description: "Partner with Central India's most trusted dairy brand"
    },
    {
      icon: Users,
      title: "Complete Support",
      description: "Marketing materials, training, and ongoing business support"
    },
    {
      icon: MapPin,
      title: "Territory Protection",
      description: "Exclusive dealership rights in your area"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-rose-600 to-red-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Become a Dealer</h1>
          <p className="text-2xl text-white/90 mb-4">
            India's Most Profitable Dairy Dealership Opportunity
          </p>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Join the fastest-growing dairy brand network and build a successful business with Arihant Milk Products
          </p>
        </div>
      </div>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Arihant Dealership?</h2>
            <p className="text-gray-600 text-lg">Be part of India's most profitable dairy distribution network</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 rounded-xl hover:shadow-lg transition-shadow duration-300">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="h-8 w-8 text-rose-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dealer Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Success Stories from Our Dealers</h2>
            <p className="text-gray-600 text-lg">Hear from our successful dealer partners across India</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {dealerTestimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md p-8 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.feedback}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-xl font-bold text-rose-600">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Register Now</h2>
            <p className="text-gray-600 text-lg">Fill out the form below and our team will contact you within 24 hours</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                  placeholder="your@email.com"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                    placeholder="Enter your city"
                  />
                </div>

                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                    placeholder="Enter your state"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="businessExperience" className="block text-sm font-medium text-gray-700 mb-2">
                  Business Experience *
                </label>
                <select
                  id="businessExperience"
                  name="businessExperience"
                  value={formData.businessExperience}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                >
                  <option value="">Select experience</option>
                  <option value="0-2">0-2 years</option>
                  <option value="2-5">2-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>

              <div>
                <label htmlFor="warehouseSpace" className="block text-sm font-medium text-gray-700 mb-2">
                  Warehouse Space Available *
                </label>
                <select
                  id="warehouseSpace"
                  name="warehouseSpace"
                  value={formData.warehouseSpace}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                >
                  <option value="">Select warehouse space</option>
                  <option value="less-500">Less than 500 sq ft</option>
                  <option value="500-1000">500-1000 sq ft</option>
                  <option value="1000-2000">1000-2000 sq ft</option>
                  <option value="2000+">2000+ sq ft</option>
                </select>
              </div>

              <div>
                <label htmlFor="currentBusiness" className="block text-sm font-medium text-gray-700 mb-2">
                  Current Business Type
                </label>
                <input
                  type="text"
                  id="currentBusiness"
                  name="currentBusiness"
                  value={formData.currentBusiness}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                  placeholder="e.g., Retail, Wholesale, Distribution"
                />
              </div>

              <div>
                <label htmlFor="investmentCapacity" className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Capacity *
                </label>
                <select
                  id="investmentCapacity"
                  name="investmentCapacity"
                  value={formData.investmentCapacity}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                >
                  <option value="">Select investment range</option>
                  <option value="1-3">1-3 Lakhs</option>
                  <option value="3-5">3-5 Lakhs</option>
                  <option value="5-10">5-10 Lakhs</option>
                  <option value="10+">10+ Lakhs</option>
                </select>
              </div>

              <div>
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none resize-none"
                  placeholder="Tell us more about your interest in becoming a dealer..."
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white py-4 text-lg font-semibold"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </div>

          {/* Direct Contact Options */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">Or reach us directly:</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/916261007909?text=Hi,%20I%20want%20to%20become%20a%20dealer%20for%20Arihant%20Milk%20Products"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors w-full sm:w-auto"
              >
                <MessageCircle className="h-5 w-5" />
                WhatsApp Us
              </a>
              <a
                href="mailto:samarthsurana@arihantmilkproducts.com?subject=Dealer%20Enquiry%20-%20Arihant%20Milk%20Products"
                className="inline-flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors w-full sm:w-auto"
              >
                <Mail className="h-5 w-5" />
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-rose-600 to-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join hundreds of successful dealers across India
          </p>
          <div className="flex items-center justify-center space-x-2 mb-8">
            <CheckCircle className="h-6 w-6 text-white" />
            <span className="text-white text-lg">24-Hour Application Response Time</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/916261007909?text=Hi,%20I%20want%20to%20become%20a%20dealer%20for%20Arihant%20Milk%20Products"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white text-green-700 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors w-full sm:w-auto"
            >
              <MessageCircle className="h-5 w-5" />
              WhatsApp Us
            </a>
            <a
              href="mailto:samarthsurana@arihantmilkproducts.com?subject=Dealer%20Enquiry%20-%20Arihant%20Milk%20Products"
              className="inline-flex items-center justify-center gap-2 bg-rose-800 text-white hover:bg-rose-900 px-8 py-3 rounded-lg font-semibold transition-colors w-full sm:w-auto"
            >
              <Mail className="h-5 w-5" />
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BecomeDealer;
